import { ReactElement, useEffect, useMemo } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  ifNumberAddCommasAndRound,
  ifNumberRoundToNDecimalPlaces,
} from '../../../utilities/numberFormatters';
import {
  customSortingFn,
  fuzzyFilter,
} from '../../tables/ultraTable/utils/ultratable.utils';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ErrorType,
  reportError,
} from '../../../utilities/errorReporting/errorReporting';

export type UnderlyingPosition = {
  name: string;
  instrument_title: string;
  client_price: number;
  delta: string;
  position_size: string;
  risk_factor: string;
  bc_exposure: number;
  fund_id: string;
  fund_name_long: string;
};

// A single fund/position breakdown row under a position.
export type PositionLevelItem = {
  name: string;
  instrument_title: string;
  client_price: number;
  delta: number;
  position_size: number;
  risk_factor: number;
  bc_exposure: number;
  fund_id: string;
  fund_name_long: string;
  [key: string]: any;
};

// Full position object passed to the nested underlying-position table.
export type PositionData = {
  position_level: PositionLevelItem[];
  name: string;
  [key: string]: any;
};

// Function for building the table data, will return list of UnderlyingPosition rows
function buildUnderlyingExposureTableData(
  data: PositionData,
): UnderlyingPosition[] | null {
  const tableData: UnderlyingPosition[] = [];

  if (!Array.isArray(data.position_level)) return null;

  data.position_level.forEach((asset: PositionLevelItem) => {
    tableData.push({
      name: asset.name,
      instrument_title: asset.instrument_title || 'N/A',
      client_price: asset.client_price,
      delta: String(asset.delta),
      position_size: String(asset.position_size),
      risk_factor: String(asset.risk_factor),
      bc_exposure: asset.bc_exposure,
      fund_id: asset.fund_id,
      fund_name_long: asset.fund_name_long,
    });
  });
  return tableData;
}


const underlyingPositionColumns: ColumnDef<UnderlyingPosition>[] = [
  {
    accessorKey: 'name',
    header: () => 'Name',
  },
  {
    accessorKey: 'instrument_title',
    header: () => 'Instrument Title',
  },
  {
    accessorKey: 'client_price',
    header: () => 'Price',
    cell: (props) => ifNumberAddCommasAndRound(props.getValue()),
  },
  {
    accessorKey: 'delta',
    header: () => 'Delta',
    cell: (props) => ifNumberRoundToNDecimalPlaces(props.getValue(), 3),
  },
  {
    accessorKey: 'position_size',
    header: () => 'Size',
    cell: (props) => ifNumberAddCommasAndRound(props.getValue()),
  },
  {
    accessorKey: 'risk_factor',
    header: () => 'Risk Factor',
  },
  {
    accessorKey: 'bc_exposure',
    header: () => 'Base Exposure',
    cell: (props) => ifNumberAddCommasAndRound(props.getValue()),
  },
  {
    accessorKey: 'fund_id',
    header: () => 'Fund ID',
  },
  {
    accessorKey: 'fund_name_long',
    header: () => 'Fund Name',
  },
];

export default function UnderlyingPositionTable({
  data: rawData,
  rowIndex,
  initialOffset,
  getScrollElement,
  scrollMargin,
}: {
  data: PositionData;
  rowIndex: number;
  initialOffset: () => number;
  getScrollElement: () => HTMLDivElement | null;
  scrollMargin: number;
}): ReactElement {

  const tableData = useMemo(
    () => buildUnderlyingExposureTableData(rawData),
    [rawData],
  );

  const hasPositionLevelError = tableData === null;
  const data = tableData ?? [];

  useEffect(() => {
    if (!hasPositionLevelError) return;

    reportError(
      'Unable to load underlying positions: position_level is not a valid array',
      new Error('position_level is missing or not an array'),
      ErrorType.DATA_PROCESSING_ERROR,
      {
        component: 'UnderlyingPositionTable',
        rowIndex,
        positionName: rawData.name,
        positionLevelType:
          rawData.position_level === undefined
            ? 'undefined'
            : rawData.position_level === null
              ? 'null'
              : typeof rawData.position_level,
      },
    );
  }, [hasPositionLevelError, rawData, rowIndex]);

  const table = useReactTable<UnderlyingPosition>({
    data,
    columns: underlyingPositionColumns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    sortingFns: {
      customSortingFn: customSortingFn,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const count = table.getRowModel().rows.length;

  const virtualizer = useVirtualizer({
    count,
    getScrollElement,
    estimateSize: () => 14,
    initialOffset,
    scrollMargin,
    overscan: 2,
  });

  const items = virtualizer.getVirtualItems();

  const HEADER_HEIGHT = 40;

  if (hasPositionLevelError) {
    return (
      <h2 className="bg-blue-50 border border-mainblue font-medium text-red-500 text-center py-2">
        Unable to load underlying positions
      </h2>
    );
  }

  return count ? (
    <div
      id={`nested-virtualizer-${rowIndex}`}
      style={{
        height: virtualizer.getTotalSize() + HEADER_HEIGHT,
        width: '100%',
        position: 'relative',
        overflowAnchor: 'none',
      }}
    >
      <table className="w-full table-fixed border border-mainblue">
        <thead className=" border-t border-gray-400 bg-blue-50">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className="flex border-t border-gray-400 py-1"
            >
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="flex w-full flex-col items-center text-mainblue justify-between py-1 text-xs font-semibold"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody
          style={{
            height: virtualizer.getTotalSize(),
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${(items[0]?.start ?? 0) - (virtualizer.options?.scrollMargin ?? 0)
                }px)`,
              overflowAnchor: 'none',
            }}
          >
            {items.map((virtualRow) => {
              const row = table.getRowModel().rows[virtualRow.index];
              return (
                <tr
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  className="flex border-t border-gray-600"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="flex w-full items-center justify-center p-1 text-center text-xs text-black"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </div>
        </tbody>
      </table>
    </div>
  ) : (
    <h2 className="mx-8 my-5 text-mainblue">No records to display</h2>
  );
}
